/**
 * Staffing flags (v2.8.0 slice 4) — DERIVED client-side from a demand's
 * headcount, approved (filled) count, application count, stage, and the
 * staffed_status override. The primary status is mutually exclusive; recruiting
 * and assessing are additive badges that layer on top during Resourcing.
 *
 * MVP approximation (no first-class Positions entity yet):
 *   - filled positions   ≈ approved_count (approved applications)
 *   - a position "posted" ≈ the demand is in Resourcing
 *   - open application    ≈ pending = application_count − approved_count
 */
import type { Demand } from '../types/demand'
import { isInResourcingBand } from './labels'

export type StaffingStatus =
  | 'not_started'
  | 'unstaffed'
  | 'partially_staffed'
  | 'fully_staffed'
  | 'closed'

export const STAFFING_STATUS_LABELS: Record<StaffingStatus, string> = {
  not_started: 'Not Started',
  unstaffed: 'Unstaffed',
  partially_staffed: 'Partially Staffed',
  fully_staffed: 'Fully Staffed',
  closed: 'Closed',
}

export interface StaffingSummary {
  status: StaffingStatus
  recruiting: boolean
  assessing: boolean
  /** Approved applications — the raw count. Derive pending from THIS. */
  approved: number
  /** What to show beside the status. Differs from `approved` only when an
   *  override declares the demand staffed by people the app never saw. */
  filled: number
  headcount: number
  /** True when a HUMAN recorded the outcome (staffed_status on/off, or the
   *  legacy `staffed` stage). False = the app is inferring from in-app
   *  applications — and since staffing overwhelmingly happens OUTSIDE the app
   *  (#1206's measured finding: 19/20 staffed demands had zero approved
   *  applications), an unconfirmed summary reads as "probably arranged
   *  externally", never as fact. */
  confirmed: boolean
}

export function deriveStaffing(demand: Demand): StaffingSummary {
  // Server-first (#1206): services/staffing.py owns the rules and derives at
  // serialization from the same inputs. The local derivation below is a
  // deploy-skew fallback kept rule-for-rule identical — both sides carry the
  // same test vectors, so a rule change on one side breaks the twin's tests.
  if (demand.staffing) return demand.staffing
  const headcount = Math.max(1, demand.headcount ?? 1)
  const approved = demand.approved_count ?? 0
  const pending = Math.max(0, (demand.application_count ?? 0) - approved)
  // This file used to own a private copy of the band. It is now shared from
  // labels.ts, because two other places needed the same three values and each
  // had written its own single-value approximation instead.
  const inResourcing = isInResourcingBand(demand.stage)

  let status: StaffingStatus
  if (demand.stage === 'closed' || demand.stage === 'resolved') {
    status = 'closed'
  } else if (demand.staffed_status === 'on' || demand.stage === 'staffed') {
    // Council override, or the legacy `staffed` stage (which literally means
    // staffed) → fully staffed regardless of the approved count.
    status = 'fully_staffed'
  } else if (demand.staffed_status === 'off') {
    status = 'unstaffed' // council override → explicitly not staffed
  } else if (!inResourcing) {
    status = 'not_started' // staffing hasn't begun (pre-Resourcing)
  } else if (approved >= headcount) {
    status = 'fully_staffed'
  } else if (approved > 0) {
    status = 'partially_staffed'
  } else {
    status = 'unstaffed'
  }

  // What the user sees next to the status (ticket #16). `approved` counts
  // APPROVED APPLICATIONS, and `staffed_status: 'on'` (like the legacy
  // `staffed` stage) exists precisely for a demand staffed outside the app —
  // so a council member marking one staffed got a "Fully Staffed" badge sitting
  // next to "0/1 staffed", which both contradicts itself and under-reports the
  // demand.
  //
  // Only the fully-staffed direction is adjusted. The `off` override is not its
  // mirror image: it asserts the demand still needs staffing, but any approved
  // people genuinely exist, and zeroing the count to match would destroy that.
  // Over-staffing (approved > headcount) is likewise left visible.
  const filled = status === 'fully_staffed' ? Math.max(approved, headcount) : approved

  return {
    status,
    // Recruiting/assessing only make sense while actively resourcing + not full.
    recruiting: inResourcing && status !== 'fully_staffed' && status !== 'closed',
    assessing: inResourcing && pending > 0,
    approved,
    filled,
    headcount,
    confirmed: demand.staffed_status === 'on' || demand.staffed_status === 'off' || demand.stage === 'staffed',
  }
}
